import React, { useState, useEffect } from 'react';
import { groupsAPI, goalsAPI } from '../services/api';

const GoalsManager = () => {
  const [groups, setGroups] = useState([]);
  const [goals, setGoals] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [newGoal, setNewGoal] = useState({ memberId: '', goalText: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchGroups();
    fetchAllGoals();
  }, []);

  useEffect(() => {
    fetchAllGoals();
  }, [selectedDate, selectedGroup]);

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAll();
      setGroups(response.data);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGoals = async () => {
    try {
      let response;
      if (selectedGroup) {
        response = await goalsAPI.getByGroup(selectedGroup);
      } else {
        response = await goalsAPI.getAll();
      }
      
      const filteredGoals = response.data.filter(goal => 
        goal.meeting_date === selectedDate
      );
      setGoals(filteredGoals);
    } catch (error) {
      console.error('Error fetching goals:', error);
      setGoals([]);
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    
    console.log('Adding goal:', {
      member_id: newGoal.memberId,
      goal_text: newGoal.goalText,
      meeting_date: selectedDate
    });
    
    try {
      const response = await goalsAPI.create({
        member_id: newGoal.memberId,
        goal_text: newGoal.goalText,
        meeting_date: selectedDate
      });
      console.log('Goal added successfully:', response);
      setNewGoal({ memberId: '', goalText: '' });
      setMessage('Goal added successfully!');
      await fetchAllGoals();
    } catch (error) {
      console.error('Error adding goal:', error);
      setMessage('Error adding goal: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableMembers = () => {
    if (!selectedGroup) return [];
    const group = groups.find(g => g.id.toString() === selectedGroup);
    return group ? group.members.filter(m => m) : [];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Goals Manager</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Group
            </label>
            <select
              value={selectedGroup}
              onChange={(e) => setSelectedGroup(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Groups</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Meeting Month
            </label>
            <input
              type="month"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Add Goal Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">Add New Goal</h2>
        <form onSubmit={handleAddGoal}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Group
              </label>
              <select
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setNewGoal({...newGoal, memberId: ''});
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a group</option>
                {groups.map(group => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Member
              </label>
              <select
                value={newGoal.memberId}
                onChange={(e) => setNewGoal({...newGoal, memberId: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={!selectedGroup}
              >
                <option value="">Select a member</option>
                {getAvailableMembers().map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Goal
              </label>
              <div className="flex">
                <input
                  type="text"
                  value={newGoal.goalText}
                  onChange={(e) => setNewGoal({...newGoal, goalText: e.target.value})}
                  placeholder="Enter goal..."
                  className="flex-1 p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 text-white px-4 py-2 rounded-r-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        </form>
        
        {message && (
          <div className={`mt-4 p-3 rounded-md ${message.includes('Error') || message.includes('error') 
            ? 'bg-red-50 text-red-700 border border-red-200' 
            : 'bg-green-50 text-green-700 border border-green-200'}`}>
            {message}
          </div>
        )}
      </div>

      {/* Goals List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">
            Goals for {selectedDate}
            {selectedGroup && ` - ${groups.find(g => g.id.toString() === selectedGroup)?.name}`}
          </h2>
        </div>
        
        <div className="divide-y divide-gray-200">
          {goals.length > 0 ? (
            goals.map((goal, index) => (
              <div key={index} className="px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">
                      {goal.group_name} - {goal.member_name}
                    </div>
                    <div className="text-gray-900">{goal.goal_text}</div>
                  </div>
                  <div className="text-sm text-gray-400 ml-4">
                    {new Date(goal.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center text-gray-500">
              No goals found for the selected criteria.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalsManager;